import React, { useState, useMemo } from "react";
import { Table, DatePicker, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { DollarSign } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportItemProfit } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const ItemProfitReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["item-profit", startDateStr, endDateStr],
    queryFn: () =>
      GetSalesReportItemProfit({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const revenue = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_revenue || 0),
      0,
    );
    const cost = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_cost || 0),
      0,
    );
    const profit = revenue - cost;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    return [
      {
        title: "Total Cost of Goods",
        value: formatLKR(cost),
        colorClass: "text-gray-600",
      },
      {
        title: "Gross Sales Revenue",
        value: formatLKR(revenue),
        colorClass: "text-blue-600",
      },
      {
        title: "Net Gross Profit",
        value: formatLKR(profit),
        colorClass: profit >= 0 ? "text-emerald-600" : "text-rose-600",
      },
      {
        title: "Avg Profit Margin",
        value: `${margin.toFixed(2)}%`,
        colorClass: "text-indigo-600",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Product Title",
      dataIndex: "product_title",
      key: "product_title",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">{v}</span>
          <span className="text-[10px] text-gray-400 font-medium">
            SKU: {record.product_sku}
          </span>
        </div>
      ),
    },
    {
      title: "Qty Sold",
      dataIndex: "quantity_sold",
      key: "quantity_sold",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-700">{v}</span>
      ),
    },
    {
      title: "Avg Cost",
      dataIndex: "avg_cost_price",
      key: "avg_cost_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-500">LKR {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Avg Selling",
      dataIndex: "avg_selling_price",
      key: "avg_selling_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-755">LKR {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-600">LKR {v.toLocaleString()}</span>
      ),
    },
    {
      title: "Revenue",
      dataIndex: "total_revenue",
      key: "total_revenue",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-blue-600">
          LKR {v.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Profit",
      dataIndex: "total_profit",
      key: "total_profit",
      align: "right" as const,
      render: (v: number) => (
        <span
          className={`text-xs font-extrabold ${v >= 0 ? "text-emerald-600" : "text-rose-500"}`}
        >
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Margin %",
      dataIndex: "margin",
      key: "margin",
      align: "right" as const,
      render: (v: number) => (
        <Tag
          color={v >= 15 ? "success" : "default"}
          className="font-bold text-xs"
        >
          {v.toFixed(1)}%
        </Tag>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Item Profit Report"
      description="Analyze individual sold item costs, selling prices, revenues, and net margins."
      icon={<DollarSign className="w-6 h-6 text-blue-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.product_sku || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
